import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUrlState } from "@/lib/navState";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MASTER_SECTIONS, MASTER_RESOURCES } from "@/lib/masterConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { UserAudit } from "@/components/UserAudit";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BrandModelSelect } from "@/components/BrandModelSelect";
import { Database, Building2, Handshake, Car, Landmark, Search, Plus, Pencil, ChevronLeft, ChevronRight, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Building2, Handshake, Car, Landmark };

function fieldVal(row, key) { const v = row?.[key]; return v === undefined || v === null || v === "" ? "" : v; }

// ---------- Add / Edit form for a generic resource ----------
function ResourceForm({ resource, cfg, refData, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => editing ? { ...editing } : cfg.fields.reduce((a, x) => ({ ...a, [x.key]: x.type === "bool" ? (x.default ?? false) : "" }), {}));
  const [warn, setWarn] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async (force = false) => {
    setWarn("");
    for (const fld of cfg.fields) if (fld.required && !String(f[fld.key] ?? "").trim()) { toast.error(`${fld.label} wajib diisi`); return; }
    setSaving(true);
    try {
      const body = { ...f };
      cfg.fields.forEach((fld) => { if (fld.type === "number") body[fld.key] = Number(body[fld.key]) || 0; });
      if (editing) await api.put(`/master/${resource}/${editing.id}`, body);
      else await api.post(`/master/${resource}?force=${force}`, body);
      toast.success(editing ? "Master data diperbarui" : "Master data ditambahkan");
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && typeof d === "object" && d.type === "name_warning") { setWarn(d.message); }
      else toast.error(formatApiErrorDetail(d) || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} {cfg.label}</DialogTitle>
          <DialogDescription>Data master dipakai bersama seluruh modul. Hindari duplikasi.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {cfg.fields.map((fld) => (
            <div key={fld.key} className={fld.type === "select" || fld.type === "ref" || fld.key === "address" ? "sm:col-span-2" : ""}>
              <Label className="text-xs">{fld.label}{fld.required ? " *" : ""}</Label>
              {fld.type === "select" ? (
                <Select value={f[fld.key] || ""} onValueChange={(v) => set(fld.key, v)}>
                  <SelectTrigger data-testid={`md-field-${fld.key}`} className="mt-1.5"><SelectValue placeholder="Pilih…" /></SelectTrigger>
                  <SelectContent>{fld.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : fld.type === "bool" ? (
                <Select value={f[fld.key] === true ? "true" : f[fld.key] === false ? "false" : ""} onValueChange={(v) => set(fld.key, v === "true")}>
                  <SelectTrigger data-testid={`md-field-${fld.key}`} className="mt-1.5"><SelectValue placeholder="Pilih…" /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              ) : fld.type === "ref" ? (
                <Select value={f[fld.key] || ""} onValueChange={(v) => set(fld.key, v)}>
                  <SelectTrigger data-testid={`md-field-${fld.key}`} className="mt-1.5"><SelectValue placeholder="Pilih…" /></SelectTrigger>
                  <SelectContent>
                    {(refData[fld.refResource] || []).map((o) => <SelectItem key={o.id} value={o.id}>{fieldVal(o, fld.refPrimary)} · {fieldVal(o, fld.refSecondary)}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid={`md-field-${fld.key}`} type={fld.type === "number" ? "number" : "text"} value={f[fld.key] ?? ""} onChange={(e) => set(fld.key, e.target.value)} className="mt-1.5" />
              )}
            </div>
          ))}
        </div>
        {warn && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1"><p className="font-semibold">{warn}</p>
              <button data-testid="md-force-save" onClick={() => save(true)} className="mt-2 rounded-md bg-amber-600 px-3 py-1 font-semibold text-white hover:bg-amber-700">Tambah tetap</button>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button data-testid="md-save-btn" disabled={saving} onClick={() => save(false)} className="bg-[#111827] text-white hover:bg-[#1F2937]">{saving ? "Menyimpan…" : "Simpan"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- User & Role manager (edits existing auth users, no auth logic) ----------
function UserRoleManager() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [emps, setEmps] = useState([]);
  const [locs, setLocs] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(null);
  const [pw, setPw] = useState("");
  const load = useCallback(async () => {
    try {
      const [{ data: u }, { data: r }, { data: e }, { data: l }] = await Promise.all([
        api.get(`/master-users`, { params: { q } }),
        api.get(`/master/roles/list`),
        api.get(`/master/employee`, { params: { active: "true" } }),
        api.get(`/master/location`),
      ]);
      setRows(u || []); setRoles(r || []); setEmps(e || []); setLocs(l || []);
    } catch (er) { /* ignore */ }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const locName = (val) => {
    if (!val) return "—";
    const byId = locs.find((x) => x.id === val);
    if (byId) return byId.location_name || byId.location_code;
    const byCode = locs.find((x) => x.location_code === val);
    if (byCode) return byCode.location_name || byCode.location_code;
    return val;
  };

  const saveNew = async () => {
    if (!adding.employee_id) { toast.error("Employee wajib dipilih dari Employee Master"); return; }
    if (!adding.role_name) { toast.error("Role wajib dipilih"); return; }
    if (!adding.email) { toast.error("Email wajib diisi"); return; }
    if (adding.password && adding.password.length < 8) { toast.error("Password minimal 8 karakter"); return; }
    try {
      const { data } = await api.post(`/master-users`, adding);
      toast.success(data.temp_password ? `User ${data.user_id} dibuat. Password sementara: ${data.temp_password}` : `User ${data.user_id} dibuat`);
      setAdding(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/master-users/${editing.id}`, { role_name: editing.role_name, active: editing.active !== false });
      toast.success("User diperbarui"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1"><Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><Input data-testid="md-users-search" placeholder="Cari user…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-10" /></div>
        <Button data-testid="md-add-user-btn" onClick={() => setAdding({ name: "", email: "", password: "", role_name: "", employee_id: "", location: "", phone: "", force_change: true, active: true })} className="h-11 bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]"><Plus size={16} className="mr-1" />Add User</Button>
      </div>
      <UserAudit />
      <div className="mt-3 space-y-2">
        {[...new Set(rows.map((u) => u.division))].sort().map((div) => (
        <div key={div} data-testid={`md-division-${div.slice(0, 2)}`} className="space-y-2">
          <div className="pt-2 text-[11px] font-bold uppercase tracking-[0.15em] text-gray-500">{div} · {rows.filter((u) => u.division === div).length}</div>
        {rows.filter((u) => u.division === div).map((u) => (
          <div key={u.id} data-testid={`md-user-${u.id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{u.employee_name || u.name || u.username}</span>
              {u.role_name && <span className="shrink-0 rounded-full bg-[#111827] px-2 py-0.5 text-[10px] font-semibold text-[#3B82F6]">{u.role_name}</span>}
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{u.active ? "Active" : "Inactive"}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-gray-500">
              {u.user_id && <span className="font-mono font-semibold text-gray-700">{u.user_id}</span>}<span>· {u.username}</span>{(u.location_name || u.location) && <span>· {u.location_name || u.location}</span>}{u.employee_id && <span>· Emp {u.employee_id}</span>}<span>· auth: {u.auth_role || "—"}</span>
            </div>
            <div data-testid={`md-user-perm-${u.id}`} className="mt-1 text-[11px] text-gray-500">Permission: {u.permission}</div>
            <div className="mt-2 flex justify-end"><Button data-testid={`md-user-edit-${u.id}`} size="sm" variant="outline" onClick={() => setEditing(u)}><Pencil size={13} className="mr-1" />Edit Role</Button></div>
          </div>
        ))}
        </div>
        ))}
        {rows.length === 0 && <p className="py-10 text-center text-sm text-gray-400">Tidak ada user.</p>}
      </div>
      {adding && (
        <Dialog open onOpenChange={(o) => !o && setAdding(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Add User</DialogTitle><DialogDescription>1 Karyawan = 1 Akun = 1 Role. User ID dibuat otomatis. Kosongkan password untuk auto-generate.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Employee <span className="text-red-500">*</span> (dari Employee Master aktif)</Label>
                <Select value={adding.employee_id || ""} onValueChange={(v) => { const e = emps.find((x) => (x.employee_id || x.id) === v); setAdding((s) => ({ ...s, employee_id: v, name: e?.employee_name || s.name, location: e?.location || s.location, email: e?.email || s.email })); }}>
                  <SelectTrigger data-testid="add-user-emp" className="mt-1.5"><SelectValue placeholder="Pilih karyawan…" /></SelectTrigger>
                  <SelectContent>{emps.map((e) => <SelectItem key={e.employee_id || e.id} value={e.employee_id || e.id}>{e.employee_name || e.name}{e.employee_id ? ` (${e.employee_id})` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Employee ID</Label><Input data-testid="add-user-empid" value={adding.employee_id || ""} disabled className="mt-1.5 bg-gray-50" /></div>
                <div><Label className="text-xs">User ID</Label><Input value="otomatis (USR-###)" disabled className="mt-1.5 bg-gray-50 text-gray-400" /></div>
              </div>
              <div><Label className="text-xs">Employee Name</Label><Input data-testid="add-user-name" value={adding.name} disabled className="mt-1.5 bg-gray-50" /></div>
              <div><Label className="text-xs">Email <span className="text-red-500">*</span></Label><Input data-testid="add-user-email" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} placeholder="wajib, unik" className="mt-1.5" /></div>
              <div><Label className="text-xs">Nomor Telepon</Label><Input data-testid="add-user-phone" value={adding.phone || ""} onChange={(e) => setAdding({ ...adding, phone: e.target.value })} placeholder="08xxxxxxxxxx" className="mt-1.5" /></div>
              <div><Label className="text-xs">Role <span className="text-red-500">*</span></Label>
                <Select value={adding.role_name || ""} onValueChange={(v) => setAdding({ ...adding, role_name: v })}>
                  <SelectTrigger data-testid="add-user-role" className="mt-1.5"><SelectValue placeholder="Pilih role…" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Location</Label><Input data-testid="add-user-loc" value={locName(adding.location)} disabled className="mt-1.5 bg-gray-50" /></div>
                <div><Label className="text-xs">Password (min 8, opsional)</Label><PasswordInput data-testid="add-user-pw" value={adding.password} onChange={(e) => setAdding({ ...adding, password: e.target.value })} placeholder="auto-generate" className="mt-1.5" /></div>
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-700"><input data-testid="add-user-force-change" type="checkbox" checked={adding.force_change !== false} onChange={(e) => setAdding({ ...adding, force_change: e.target.checked })} className="h-4 w-4" />Wajib ganti password saat login pertama</label>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setAdding(null)}>Batal</Button><Button data-testid="add-user-save" onClick={saveNew} className="bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]">Buat Akun</Button></div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Edit User & Role</DialogTitle><DialogDescription>Hanya Role, Status & Reset Password yang dapat diubah. Relasi Employee/Email/User ID bersifat immutable.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Employee Name</Label><Input value={editing.name || ""} disabled className="mt-1.5 bg-gray-50" /></div>
                <div><Label className="text-xs">Employee ID</Label><Input value={editing.employee_id || "—"} disabled className="mt-1.5 bg-gray-50" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">User ID</Label><Input data-testid="md-user-userid" value={editing.user_id || "—"} disabled className="mt-1.5 bg-gray-50 font-mono" /></div>
                <div><Label className="text-xs">Location</Label><Input data-testid="md-user-loc" value={editing.location_name || locName(editing.location)} disabled className="mt-1.5 bg-gray-50" /></div>
              </div>
              <div><Label className="text-xs">Email / Username</Label><Input value={editing.username || editing.email || ""} disabled className="mt-1.5 bg-gray-50" /></div>
              <div><Label className="text-xs">Role</Label>
                <Select value={editing.role_name || ""} onValueChange={(v) => setEditing({ ...editing, role_name: v })}>
                  <SelectTrigger data-testid="md-user-role" className="mt-1.5"><SelectValue placeholder="Pilih role…" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2"><input type="checkbox" data-testid="md-user-active" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} id="ua" /><Label htmlFor="ua" className="text-xs">Active (uncheck = deactivate / blokir login, history tetap tersimpan)</Label></div>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-2">
                <Label className="text-xs font-semibold">Reset Password</Label>
                <div className="mt-1.5 flex gap-2">
                  <PasswordInput data-testid="md-user-newpw" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password baru (min 8)" className="h-9" />
                  <Button data-testid="md-user-reset" size="sm" variant="outline" onClick={async () => { if (pw.length < 8) { toast.error("Password minimal 8 karakter"); return; } try { await api.post(`/master-users/${editing.id}/reset-password`, { new_password: pw }); toast.success("Password direset"); setPw(""); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }}>Reset</Button>
                </div>
              </div>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => { setEditing(null); setPw(""); }}>Batal</Button>
                <Button data-testid="md-user-save" className="bg-[#111827] text-white hover:bg-[#1F2937]" onClick={saveEdit}>Simpan</Button></div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------- Generic resource manager ----------
function ResourceManager({ resource }) {
  const cfg = MASTER_RESOURCES[resource];
  const [rows, setRows] = useState([]);
  const [refData, setRefData] = useState({});
  const [q, setQ] = useUrlState("q", "");
  const [active, setActive] = useUrlState("status", "all");
  const [form, setForm] = useState(null); // {editing} or {} for add

  const load = useCallback(async () => {
    if (cfg.special) return;
    try { const { data } = await api.get(`/master/${resource}`, { params: { q, active } }); setRows(data || []); } catch (e) { /* ignore */ }
  }, [resource, q, active, cfg.special]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refs = [...new Set((cfg.fields || []).filter((x) => x.type === "ref").map((x) => x.refResource))];
    if (refs.length === 0) { setRefData({}); return; }
    Promise.all(refs.map((r) => api.get(`/master/${r}`, { params: { active: "true" } }).then((res) => [r, res.data]).catch(() => [r, []]))).then((pairs) => setRefData(Object.fromEntries(pairs)));
  }, [cfg]);

  if (cfg.special === "users") return <UserRoleManager />;

  const refLabel = (fld, id) => { const o = (refData[fld.refResource] || []).find((x) => x.id === id); return o ? `${fieldVal(o, fld.refPrimary)} · ${fieldVal(o, fld.refSecondary)}` : "—"; };

  const toggleActive = async (row) => {
    try { await api.patch(`/master/${resource}/${row.id}/active`, { active: !(row.active !== false) }); toast.success(row.active !== false ? "Dinonaktifkan" : "Diaktifkan"); load(); } catch (e) { toast.error("Gagal"); }
  };

  return (
    <div>
      <div className="relative"><Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><Input data-testid="md-search" placeholder={`Cari ${cfg.label}…`} value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-10" /></div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex gap-1.5">
          {[["all", "All"], ["true", "Active"], ["false", "Inactive"]].map(([k, l]) => (
            <button key={k} data-testid={`md-filter-${k}`} onClick={() => setActive(k)} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active === k ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>{l}</button>
          ))}
        </div>
        <Button data-testid="md-add-btn" size="sm" onClick={() => setForm({ editing: null })} className="bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]"><Plus size={15} className="mr-1" />Add</Button>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((row) => (
          <div key={row.id} data-testid={`md-row-${row.id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px] font-bold text-gray-700">{fieldVal(row, cfg.primary) || "—"}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{fieldVal(row, cfg.secondary) || "—"}</span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${row.active !== false ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{row.active !== false ? "Active" : "Inactive"}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
              {(cfg.fields || []).filter((fl) => ![cfg.primary, cfg.secondary].includes(fl.key)).map((fl) => {
                const v = fl.type === "ref" ? (row[fl.key] ? refLabel(fl, row[fl.key]) : "") : fl.type === "bool" ? (row[fl.key] === true ? "Yes" : row[fl.key] === false ? "No" : "") : fieldVal(row, fl.key);
                return v ? <span key={fl.key}>{fl.label}: <span className="font-medium text-gray-700">{v}</span></span> : null;
              })}
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button data-testid={`md-edit-${row.id}`} size="sm" variant="outline" onClick={() => setForm({ editing: row })}><Pencil size={13} className="mr-1" />Edit</Button>
              {row.active !== false
                ? <Button data-testid={`md-deactivate-${row.id}`} size="sm" variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-50" onClick={() => toggleActive(row)}><XCircle size={13} className="mr-1" />Deactivate</Button>
                : <Button data-testid={`md-activate-${row.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => toggleActive(row)}><CheckCircle2 size={13} className="mr-1" />Activate</Button>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="py-10 text-center text-sm text-gray-400">Belum ada data. Klik Add untuk menambah.</p>}
      </div>
      {form && <ResourceForm resource={resource} cfg={cfg} refData={refData} editing={form.editing} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function VehicleMaster() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [quickVin, setQuickVin] = useState({});
  const navigate = useNavigate();
  const load = useCallback(async () => {
    try { const { data } = await api.get("/vehicles", { params: { q } }); setRows(data || []); } catch (e) { /* ignore */ }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const saveVeh = async () => {
    try {
      await api.put(`/vehicles/${edit.vehicle_id}`, edit);
      toast.success("Vehicle diperbarui"); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal"); }
  };

  const saveVin = async (vid) => {
    const val = (quickVin[vid] || "").trim();
    if (!val) { toast.error("VIN belum diisi"); return; }
    try {
      await api.put(`/vehicles/${vid}`, { vin: val });
      toast.success("VIN tersimpan");
      setQuickVin((s) => { const n = { ...s }; delete n[vid]; return n; });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan VIN"); }
  };

  const withVin = rows.filter((r) => r.vin).length;
  const missing = rows.length - withVin;
  const visibleRows = onlyMissing ? rows.filter((r) => !r.vin) : rows;

  return (
    <div>
      <div className="relative"><Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><Input data-testid="veh-search" placeholder="Cari Nopol, VIN, VEH ID, pemilik…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-10" /></div>

      <div className="mt-3 rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between text-sm"><span className="font-semibold text-gray-800">Kelengkapan VIN / No. Rangka</span><span data-testid="veh-vin-progress" className="text-gray-600">{withVin}/{rows.length} lengkap</span></div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100"><span className="block h-full rounded-full bg-[#3B82F6] transition-all" style={{ width: `${rows.length ? (withVin / rows.length) * 100 : 0}%` }} /></div>
        <div className="mt-2 flex gap-2">
          <button data-testid="veh-filter-all" onClick={() => setOnlyMissing(false)} className={`rounded-full px-3 py-1 text-xs font-semibold ${!onlyMissing ? "bg-[#111827] text-white" : "bg-gray-100 text-gray-600"}`}>Semua ({rows.length})</button>
          <button data-testid="veh-filter-missing" onClick={() => setOnlyMissing(true)} className={`rounded-full px-3 py-1 text-xs font-semibold ${onlyMissing ? "bg-amber-600 text-white" : "bg-gray-100 text-gray-600"}`}>Tanpa VIN ({missing})</button>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {visibleRows.map((v) => (
          <div key={v.vehicle_id} data-testid={`veh-row-${v.vehicle_id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px] font-bold text-gray-700">{v.vehicle_id}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{v.license_plate} · {[v.brand, v.model].filter(Boolean).join(" ")}</span>
              <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">{v.visit_count} visit</span>
              {v.needs_review && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Needs Review</span>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
              {v.vin ? (
                <span>VIN: <b className="text-gray-700">{v.vin}</b></span>
              ) : (
                <span className="flex items-center gap-1" data-testid={`veh-quickvin-${v.vehicle_id}`}>
                  <input value={quickVin[v.vehicle_id] || ""} onChange={(e) => setQuickVin((s) => ({ ...s, [v.vehicle_id]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") saveVin(v.vehicle_id); }} placeholder="Isi VIN / No. Rangka" className="h-7 w-48 rounded border border-amber-300 bg-amber-50 px-2 text-[11px] uppercase outline-none focus:border-amber-500" />
                  <button data-testid={`veh-savevin-${v.vehicle_id}`} onClick={() => saveVin(v.vehicle_id)} className="rounded bg-[#3B82F6] px-2 py-1 text-[11px] font-semibold text-gray-900 hover:bg-[#C5A059]">Simpan</button>
                </span>
              )}
              <span>No Mesin: <b className="text-gray-700">{v.engine_no || "—"}</b></span>
              <span>Pemilik: <b className="text-gray-700">{v.owner_name || "—"}</b></span>
              <span>HP: <b className="text-gray-700">{v.phone || "—"}</b></span>
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button data-testid={`veh-history-${v.vehicle_id}`} size="sm" variant="outline" onClick={() => navigate(`/history?vehicle=${v.vehicle_id}`)}>History</Button>
              <Button data-testid={`veh-edit-${v.vehicle_id}`} size="sm" variant="outline" onClick={() => setEdit({ ...v })}><Pencil size={13} className="mr-1" />Edit</Button>
            </div>
          </div>
        ))}
        {visibleRows.length === 0 && <p className="py-10 text-center text-sm text-gray-400">{onlyMissing ? "Semua kendaraan sudah memiliki VIN 🎉" : "Belum ada kendaraan. Vehicle dibuat otomatis saat Claim dibuat."}</p>}
      </div>
      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Edit {edit.vehicle_id}</DialogTitle><DialogDescription>Nopol boleh berubah; Vehicle ID tetap sebagai identitas permanen.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[["license_plate", "Nopol"], ["vin", "VIN / No. Rangka"], ["engine_no", "No. Mesin"]].map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`veh-field-${k}`} value={edit[k] || ""} onChange={(e) => setEdit((s) => ({ ...s, [k]: e.target.value }))} className="mt-1.5" /></div>
              ))}
              <BrandModelSelect brand={edit.brand} model={edit.model} onChange={(b, m) => setEdit((s) => ({ ...s, brand: b, model: m }))} size="h-10" testidPrefix="veh" />
              {[["year", "Tahun"], ["color", "Warna"], ["owner_name", "Pemilik"], ["phone", "No. HP"]].map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`veh-field-${k}`} value={edit[k] || ""} onChange={(e) => setEdit((s) => ({ ...s, [k]: e.target.value }))} className="mt-1.5" /></div>
              ))}
              <div className="sm:col-span-2"><Label className="text-xs">Needs Review</Label>
                <Select value={edit.needs_review ? "true" : "false"} onValueChange={(v) => setEdit((s) => ({ ...s, needs_review: v === "true" }))}>
                  <SelectTrigger data-testid="veh-field-needs_review" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setEdit(null)}>Batal</Button><Button data-testid="veh-save" onClick={saveVeh} className="bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]">Simpan</Button></div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function MasterData() {
  const [selected, setSelected] = useUrlState("md", "", true);

  if (selected === "__vehicles__") {
    return (
      <div>
        <button data-testid="md-back-btn" onClick={() => setSelected(null)} className="mb-3 flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-[#111827]"><ChevronLeft size={16} />Master Data</button>
        <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl">Vehicle Master</h1>
        <p className="mt-1 text-sm text-gray-500">Identitas kendaraan permanen (VEH-######). 1 Vehicle → banyak Claim → banyak WO. Nopol = pencarian, bukan primary key.</p>
        <div className="mt-5"><VehicleMaster /></div>
      </div>
    );
  }

  if (selected) {
    const cfg = MASTER_RESOURCES[selected];
    return (
      <div>
        <button data-testid="md-back-btn" onClick={() => setSelected(null)} className="mb-3 flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-[#111827]"><ChevronLeft size={16} />Master Data</button>
        <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl">{cfg.label}</h1>
        <p className="mt-1 text-sm text-gray-500">Kelola data master · Add / Edit / Activate / Deactivate (tanpa hapus permanen)</p>
        <div className="mt-5"><ResourceManager resource={selected} /></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl"><Database size={26} className="mr-2 inline text-[#3B82F6]" />Master Data</h1>
      <p className="mt-1 text-sm text-gray-500">Satu master record → banyak transaksi. Referensi terpusat untuk seluruh modul.</p>
      <div className="mt-6 space-y-6">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500"><Car size={14} className="text-[#3B82F6]" />Vehicle</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <button data-testid="md-open-vehicles" onClick={() => setSelected("__vehicles__")} className="flex items-center justify-between rounded-lg border border-gray-100 bg-white p-4 text-left shadow-sm transition-colors hover:border-[#3B82F6] hover:bg-amber-50/30">
              <span className="text-sm font-semibold text-gray-800">Vehicle Master (VEH)</span>
              <ChevronRight size={16} className="text-gray-400" />
            </button>
          </div>
        </div>
        {MASTER_SECTIONS.map((sec) => {
          const Icon = ICONS[sec.icon] || Database;
          return (
            <div key={sec.key}>
              <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500"><Icon size={14} className="text-[#3B82F6]" />{sec.label}</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {sec.resources.map((r) => (
                  <button key={r} data-testid={`md-open-${r}`} onClick={() => setSelected(r)} className="flex items-center justify-between rounded-lg border border-gray-100 bg-white p-4 text-left shadow-sm transition-colors hover:border-[#3B82F6] hover:bg-amber-50/30">
                    <span className="text-sm font-semibold text-gray-800">{MASTER_RESOURCES[r].label}</span>
                    <ChevronRight size={16} className="text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
